"use client";

import { FrontDoorNotice } from "@/app/(front-door)/_components/FrontDoorNotice";
import {
  ReferPanel,
  TherapistInvitePanel,
} from "@/app/(front-door)/_components/FrontDoorPanels";
import { StaffSignInForm } from "@/app/(front-door)/_components/StaffSignInForm";
import { StaffSignUpForm } from "@/app/(front-door)/_components/StaffSignUpForm";
import {
  FRONT_DOOR_LINK_CLASS,
  FRONT_DOOR_PILL_CLASS,
  FRONT_DOOR_PILL_SELECTED_CLASS,
  FRONT_DOOR_PILL_UNSELECTED_CLASS,
  FRONT_DOOR_TAB_CLASS,
  FRONT_DOOR_TAB_SELECTED_CLASS,
  FRONT_DOOR_TAB_UNSELECTED_CLASS,
} from "@/app/(front-door)/_components/styles";
import { PortalOtpLoginForm } from "@/app/(portal)/portal/_components/PortalOtpLoginForm";
import { MarbleLink as Link } from "@/app/_components/ui/marble-link";
import { IS_PREVIEW } from "@/config";
import {
  FRONT_DOOR_HEADING,
  FRONT_DOOR_ROLES,
  getFrontDoorReferHref,
  getProductionFrontDoorHref,
  isFamilyFrontDoorRole,
  isFrontDoorSignUpRole,
  type FrontDoorRole,
  type FrontDoorTab,
} from "@/lib/modules/auth/front-door";
import type { PortalParams } from "@/lib/portal-params";
import { cn } from "@/lib/utils";
import { usePathname, useSearchParams } from "next/navigation";
import { useState } from "react";

/**
 * Sign in and sign up for every role, at `/auth/login`, `/auth/register` and
 * `/portal/login`. Picking a pill resets to the Sign in tab.
 */
export const FrontDoor = ({
  initialRole,
  initialTab,
  isPortal = false,
  isSessionExpired = false,
  callbackUrl,
  portalParams,
  schoolId,
  schoolName,
  inviteEmail,
  isInviteInvalid = false,
}: {
  initialRole: FrontDoorRole;
  initialTab: FrontDoorTab;
  /** `/portal/login`: family sign-in with the pills hidden. */
  isPortal?: boolean;
  isSessionExpired?: boolean;
  callbackUrl?: string;
  portalParams: PortalParams;
  /** From a `?schoolId=` sign-up link. */
  schoolId?: number;
  /** The linked school, shown on a school worker sign-up. */
  schoolName?: string;
  inviteEmail?: string;
  isInviteInvalid?: boolean;
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [role, setRole] = useState(initialRole);
  const [tab, setTab] = useState(initialTab);
  const [arePillsShown, setArePillsShown] = useState(!isPortal);
  const [isExpiredNoticeShown, setIsExpiredNoticeShown] =
    useState(isSessionExpired);
  const [invite, setInvite] = useState({
    email: inviteEmail,
    isInvalid: isInviteInvalid,
  });

  const isFamily = isFamilyFrontDoorRole(role);

  const handlePickRole = (nextRole: FrontDoorRole) => {
    setRole(nextRole);
    setTab("sign-in");
    setIsExpiredNoticeShown(false);
  };

  const tabs: { value: FrontDoorTab; label: string }[] = [
    { value: "sign-in", label: "Sign in" },
    { value: "new", label: "New to Marble" },
  ];

  return (
    <div>
      {isExpiredNoticeShown && tab === "sign-in" && (
        <FrontDoorNotice
          tone="warning"
          title="Session expired"
          className="mb-[22px]"
        >
          <p>
            Your session has expired for security purposes, please sign back in.
          </p>
          <p className="mt-2">
            {"Forgot your password? "}
            <Link
              href="/auth/password-reset"
              className="font-semibold underline"
            >
              Reset it here
            </Link>
            .
          </p>
        </FrontDoorNotice>
      )}

      <div className="mb-[26px] text-center">
        <h1 className="text-balance font-martina text-[32px] font-medium leading-[1.12] tracking-[-0.02em] text-foreground md:text-[40px]">
          {FRONT_DOOR_HEADING.title}
        </h1>
      </div>

      {arePillsShown ? (
        <div className="-mx-4 mb-[26px] md:-mx-[120px]">
          <p className="mb-3 text-center text-sm font-medium text-muted-foreground">
            To get started, choose who you are
          </p>
          <div className="overflow-x-auto px-4 py-0.5 [scrollbar-width:none]">
            <div
              role="radiogroup"
              aria-label="Who are you?"
              className="mx-auto flex w-max gap-1.5"
            >
              {FRONT_DOOR_ROLES.map((option) => {
                const isSelected = option.role === role;
                return (
                  <button
                    key={option.role}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => handlePickRole(option.role)}
                    className={cn(
                      FRONT_DOOR_PILL_CLASS,
                      isSelected
                        ? FRONT_DOOR_PILL_SELECTED_CLASS
                        : FRONT_DOOR_PILL_UNSELECTED_CLASS,
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <p className="-mt-2 mb-[22px] text-center text-[14.5px] text-foreground-soft">
          {"Patient & family sign-in · "}
          <button
            type="button"
            onClick={() => setArePillsShown(true)}
            className={FRONT_DOOR_LINK_CLASS}
          >
            Not you?
          </button>
        </p>
      )}

      {role === "SCHOOL_WORKER" &&
        tab === "new" &&
        schoolName &&
        !invite.email &&
        !invite.isInvalid && (
          <div className="mb-[22px] flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-hairline bg-white px-3.5 py-2 text-sm text-foreground">
              <span>
                {"Signing up with "}
                <strong>{schoolName}</strong>
              </span>
            </span>
          </div>
        )}

      <div className="rounded-2xl border-[1.5px] border-primary-hairline bg-white px-5 pb-6 pt-5 md:px-[30px] md:pb-[30px] md:pt-6">
        <div
          role="tablist"
          aria-label="Sign in or get started"
          className="mb-[26px] flex gap-1 rounded-xl bg-[#ebe8fb] p-1"
        >
          {tabs.map((option) => {
            const isSelected = option.value === tab;
            return (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={isSelected}
                onClick={() => setTab(option.value)}
                className={cn(
                  FRONT_DOOR_TAB_CLASS,
                  isSelected
                    ? FRONT_DOOR_TAB_SELECTED_CLASS
                    : FRONT_DOOR_TAB_UNSELECTED_CLASS,
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div role="tabpanel" className="animate-in fade-in-0 duration-200">
          {IS_PREVIEW && tab === "new" && (
            <FrontDoorNotice
              tone="warning"
              title="This is a preview environment"
              className="mb-[22px]"
            >
              {"If you are trying to sign up for a real Marble account, "}
              <a
                href={getProductionFrontDoorHref({ pathname, searchParams })}
                className="font-semibold underline"
              >
                click here
              </a>
              .
            </FrontDoorNotice>
          )}
          {tab === "sign-in" &&
            (isFamily ? (
              <PortalOtpLoginForm
                userId={portalParams.userId}
                therapistId={portalParams.therapistId}
                type={portalParams.type}
                next={portalParams.next}
              />
            ) : (
              <StaffSignInForm
                emailPlaceholder={
                  role === "SCHOOL_WORKER" ? "you@school.org" : "you@email.com"
                }
                callbackUrl={callbackUrl}
              />
            ))}
          {tab === "new" && isFamily && (
            <ReferPanel
              isSelf={role === "PATIENT"}
              referHref={getFrontDoorReferHref({
                isSelf: role === "PATIENT",
                searchParams,
              })}
              onReferSelf={
                arePillsShown || role === "PATIENT"
                  ? undefined
                  : () => setRole("PATIENT")
              }
            />
          )}
          {tab === "new" && isFrontDoorSignUpRole(role) && (
            <StaffSignUpForm
              key={role}
              role={role}
              schoolId={schoolId}
              inviteEmail={invite.email}
              isInviteInvalid={invite.isInvalid}
              onDropInvite={() =>
                setInvite({ email: undefined, isInvalid: false })
              }
              callbackUrl={callbackUrl}
            />
          )}
          {tab === "new" && role === "THERAPIST" && <TherapistInvitePanel />}
        </div>
      </div>
    </div>
  );
};
